import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AgentConversationListItem, AgentConversationDetail, AgentConversationFilter, CrmConversationContext, KnowledgeMetrics } from '../models/agent-conversation.model';
import { AgentMessage, AgentSendMessageRequest } from '../models/agent-message.model';
import {
  AgentAssignRequest,
  AgentAttachmentUploadResponse,
  AgentCloseRequest,
  AgentCreateTicketRequest,
  AgentTransferRequest
} from '../models/agent-action.model';
import { environment } from '../../../../environments/environment';

/** Subconjunto de `TicketResponse` que consume la bandeja tras crear el ticket. */
export interface TicketApiResponse {
  id: number;
  ticketNumber: string;
  subject: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgentConversationApiService {
  private apiUrl = `${environment.apiUrl}api/agent/conversations`;

  constructor(private http: HttpClient) {}

  /**
   * Los nombres de los parámetros son los del controller (`status`, `assignedToMe`,
   * `contactId`). Los `snake_case` que había aquí no los leía nadie, así que "solo las mías"
   * no filtraba y "conversaciones anteriores" del contacto devolvía la bandeja entera.
   */
  getConversations(filters?: AgentConversationFilter): Observable<AgentConversationListItem[]> {
    let params = new HttpParams();
    if (filters) {
      if (filters.status && filters.status !== 'ALL') {
        params = params.set('status', filters.status);
      }
      if (filters.assignedToMe !== undefined) {
        params = params.set('assignedToMe', filters.assignedToMe.toString());
      }
      if (filters.contactId !== undefined) {
        params = params.set('contactId', filters.contactId.toString());
      }
    }
    return this.http.get<AgentConversationListItem[]>(this.apiUrl, { params });
  }

  getConversationDetail(conversationId: number): Observable<{ conversation: AgentConversationDetail, transcript: AgentMessage[] }> {
    return this.http.get<{ conversation: AgentConversationDetail, transcript: AgentMessage[] }>(`${this.apiUrl}/${conversationId}`);
  }

  sendAgentMessage(conversationId: number, payload: AgentSendMessageRequest): Observable<AgentMessage> {
    return this.http.post<AgentMessage>(`${this.apiUrl}/${conversationId}/messages`, payload);
  }

  /** Renombra la conversación en la bandeja. `null` o cadena vacía borra el título. */
  updateTitle(conversationId: number, title: string | null): Observable<AgentConversationDetail> {
    return this.http.put<AgentConversationDetail>(`${this.apiUrl}/${conversationId}/title`, { title });
  }

  assignConversation(conversationId: number, payload: AgentAssignRequest): Observable<AgentConversationDetail> {
    return this.http.post<AgentConversationDetail>(`${this.apiUrl}/${conversationId}/assign`, payload);
  }

  transferConversation(conversationId: number, payload: AgentTransferRequest): Observable<AgentConversationDetail> {
    return this.http.post<AgentConversationDetail>(`${this.apiUrl}/${conversationId}/transfer`, payload);
  }

  closeConversation(conversationId: number, payload: AgentCloseRequest): Observable<AgentConversationDetail> {
    return this.http.post<AgentConversationDetail>(`${this.apiUrl}/${conversationId}/close`, payload);
  }

  /** RF-05: Obtiene el contexto CRM explícito de la conversación */
  getCrmContext(conversationId: number): Observable<CrmConversationContext> {
    return this.http.get<CrmConversationContext>(`${this.apiUrl}/${conversationId}/crm-context`);
  }

  /** RF-08: Métricas de knowledge base para un chatflow */
  getKnowledgeMetrics(chatflowId: number): Observable<KnowledgeMetrics> {
    return this.http.get<KnowledgeMetrics>(`${environment.apiUrl}api/chatflows/${chatflowId}/knowledge-metrics`);
  }

  /**
   * RF-11: crear ticket manual desde la bandeja de agente.
   *
   * El backend exige el cuerpo completo (`subject` es @NotBlank): el `{}` que se mandaba antes
   * moría siempre en validación y la bandeja lo traducía a "No se pudo crear el ticket"
   * (INC_CRM_019 (8) inc68). La respuesta es un TicketResponse.
   */
  createTicketFromConversation(conversationId: number, payload: AgentCreateTicketRequest): Observable<TicketApiResponse> {
    return this.http.post<TicketApiResponse>(`${this.apiUrl}/${conversationId}/create-ticket`, payload);
  }

  /** inc71: sube el archivo y devuelve su URL pública para adjuntarla al siguiente mensaje. */
  uploadAttachment(conversationId: number, file: File): Observable<AgentAttachmentUploadResponse> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http.post<AgentAttachmentUploadResponse>(`${this.apiUrl}/${conversationId}/attachments`, form);
  }

  /** RF-14: Exportar transcripción como TXT o JSON */
  exportTranscript(conversationId: number, format: 'txt' | 'json'): Observable<Blob> {
    const params = new HttpParams().set('format', format);
    return this.http.get(`${this.apiUrl}/${conversationId}/transcript/export`, { params, responseType: 'blob' });
  }

  /** RF-14: Eliminar transcripción */
  deleteTranscript(conversationId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${this.apiUrl}/${conversationId}/privacy/delete-transcript`, {});
  }

  /** RF-14: Anonimizar conversación (requiere Admin) */
  anonymizeTranscript(conversationId: number): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(
      `${environment.apiUrl}api/admin/chatflows/conversations/${conversationId}/privacy/anonymize`, {}
    );
  }
}
